import { useState, useEffect, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import { 
  IRolePermissions, 
  SettingsPermissions, 
  UserManagementPermissions, 
  TeamAndChannelPermissions 
} from 'types/role.types'
import { useAppSelector } from 'app/hooks'

// Komponentler
import Header from './components/Header'
import Sidebar from './components/Sidebar'
import Navbar from './components/Navbar'
import Content from './components/Content'
import { SubMenuItem } from './components/Navbar'
import { icons } from 'utils/icons'
import { FORM, TITLES } from 'locales/locales'
import uploadApi from 'app/axiosApi';

const AdminPanel = () => {
  const { t } = useTranslation()
  const location = useLocation()
  // const navigate = useNavigate()
  const [activeMenuItem, setActiveMenuItem] = useState<string>('')

  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)
  const accessToken = useAppSelector((state) => state.auth.accessToken)
  
  // Dashboard
  const hasDashboardViewPermission = useMemo(() => 
    isFlagSetIn(
      [SettingsPermissions.VIEW_DASHBOARD],
      permissions.settings,
    ),
    [permissions.settings]
  )
  
  // Settings
  const hasSystemSettingViewPermission = useMemo(() => 
    isFlagSetIn(
      [SettingsPermissions.VIEW_SYSTEM_SETTINGS],
      permissions.settings,
    ),
    [permissions.settings]
  )
  
  const hasPasswordSettingsPermission = useMemo(() => 
    isFlagSetIn(
      [SettingsPermissions.VIEW_PASSWORD_SETTINGS],
      permissions.settings,
    ),
    [permissions.settings]
  )
  
  const hasEmailSettingsPermission = useMemo(() => 
    isFlagSetIn(
      [SettingsPermissions.VIEW_EMAIL_SETTINGS],
      permissions.settings,
    ),
    [permissions.settings]
  )
  
  const hasLdapSettingsPermission = useMemo(() => 
    isFlagSetIn(
      [SettingsPermissions.VIEW_LDAP_SETTINGS],
      permissions.settings,
    ),
    [permissions.settings]
  )
  
  const hasRoleSettingsPermission = useMemo(() => 
    isFlagSetIn(
      [SettingsPermissions.VIEW_ROLE_SETTINGS],
      permissions.settings,
    ),
    [permissions.settings]
  )
  
  // Management
  const hasUserViewPermission = useMemo(() => 
    isFlagSetIn(
      [UserManagementPermissions.VIEW_USER_SETTINGS],
      permissions.userManagement,
    ),
    [permissions.userManagement]
  )
  
  const hasTeamsViewPermission = useMemo(() => 
    isFlagSetIn(
      [TeamAndChannelPermissions.VIEW_TEAM_SETTINGS],
      permissions.teamAndChannel,
    ),
    [permissions.teamAndChannel]
  )
  
  const hasChannelOperationPermissions = useMemo(() => 
    isFlagSetIn(
      [TeamAndChannelPermissions.VIEW_CHANNEL_SETTINGS],
      permissions.teamAndChannel,
    ),
    [permissions.teamAndChannel]
  )

  const menuItems = useMemo(() => [
    {
      id: 'dashboard',
      title: t(TITLES.DASHBOARD_TITLE),
      color: '#e74c3c',
      active: activeMenuItem === 'dashboard',
      visible: hasDashboardViewPermission,
      path: '/admin-panel/dashboard/general-view',
      icon: icons.adminDashboardIcon
    },
    {
      id: 'settings',
      title:  t(TITLES.SETTINGS_TITLE),
      color: '#6c5ce7',
      active: activeMenuItem === 'settings',
      visible: hasSystemSettingViewPermission || hasPasswordSettingsPermission || 
              hasEmailSettingsPermission || hasLdapSettingsPermission || hasRoleSettingsPermission,
      path: '/admin-panel/settings/system',
      icon: icons.adminSettingsIcon
    },
      {
        id: 'management',
        title: t(TITLES.MANAGEMENT_TITLE),
        color: '#e84393',
        active: activeMenuItem === 'management',
        visible: hasUserViewPermission || hasTeamsViewPermission || hasChannelOperationPermissions,
        path: '/admin-panel/management/user',
        icon: icons.managementIcon
      },
      {
        id: 'mobile',
        title:t(TITLES.MOBILE_APP_SETTINGS_TITLE),
        color: '#017ab3e4',
        active: activeMenuItem === 'mobile',
        visible: hasSystemSettingViewPermission,
        path: '/admin-panel/mobile/mobile-apps',
        icon: icons.adminSettingsIcon
      },
/*       {
        id: 'notification',
        title: "Bildirim Ayarları",
        color: '#6c5ce7',
        active: activeMenuItem === 'notification',
        visible: hasSystemSettingViewPermission,
        path: '/admin-panel/notification',
        icon: icons.adminSettingsIcon
      } */
  ], [
    activeMenuItem, 
    hasDashboardViewPermission, 
    hasSystemSettingViewPermission, 
    hasPasswordSettingsPermission, 
    hasEmailSettingsPermission, 
    hasLdapSettingsPermission, 
    hasRoleSettingsPermission, 
    hasUserViewPermission, 
    hasTeamsViewPermission, 
    hasChannelOperationPermissions
  ])
  

  // Alt menü öğelerini tanımlayalım
  const getSubMenuItems = useCallback((): SubMenuItem[] => {
    switch (activeMenuItem) {
      case 'dashboard':
        return [
          {
            id: 'general-view',
            title: t(TITLES.DASHBOARD_TITLE),
            icon: icons.dashboardIcon,
            path: '/admin-panel/dashboard/general-view',
          },
/*           {
            id: 'statistics',
            title: 'İstatistikler(translate)',
            icon: icons.dashboardIcon,
            path: '/admin-panel/dashboard/statistics',
          },
          {
            id: 'reports',
            title: 'İşlem Kayıtları',
            icon: icons.dashboardIcon,
            path: '/admin-panel/dashboard/reports',
          }, */
        ]
      case 'settings':
        return [
          {
            id: 'system-settings',
            title: t(TITLES.SYSTEM_SETTINGS_TITLE),
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/settings/system',
            visible: hasSystemSettingViewPermission,
          },
          {
            id: 'password-settings',
            title: t(TITLES.PASSWORD_SETTINGS_TITLE),
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/settings/password',
            visible: hasPasswordSettingsPermission,
          },
          {
            id: 'mail-provider-settings',
            title: t(TITLES.MAIL_SETTINGS_TITLE),
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/settings/mail-provider',
            visible: hasEmailSettingsPermission,
          },
          {
            id: 'ldap-settings',
            title: t(TITLES.LDAP_SETTINGS_TITLE),
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/settings/ldap',
            visible: hasLdapSettingsPermission,
          },
          {
            id: 'rol-settings',
            title: t(TITLES.ROLES_SETTINGS_TITLE),
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/settings/role',
            visible: hasRoleSettingsPermission,
          },
          {
            id: 'mobile-web-settings',
            title: t(TITLES.MOBILE_WEB_SETTINGS_TITLE),
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/settings/mobile-web',
            visible: hasSystemSettingViewPermission,
          },
          {
            id: 'privacy-text-settings',
            title: t(FORM.KVKK),
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/settings/privacy-text',
            visible: hasSystemSettingViewPermission,
          },
          {
            id: 'file-extension-settings',
            title: t(FORM.FILE_UPLOAD_TITLE),
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/settings/file-extension',
            visible: hasSystemSettingViewPermission,
          },
        ].filter((item) => item.visible)
      case 'management':
        return [
          {
            id: 'user-management',
            title: t(TITLES.USER_SETTINGS_TITLE),
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/management/user',
            visible: hasUserViewPermission,
          },
          {
            id: 'team-management',
            title: t(TITLES.TEAMS_SETTINGS_TITLE),
            icon: icons.managementIcon,
            path: '/admin-panel/management/team',
            visible: hasTeamsViewPermission,
          },
          {
            id: 'channel-management',
            title: t(TITLES.CHANNEL_SETTINGS_TITLE),
            icon: icons.managementIcon,
            path: '/admin-panel/management/channel',
            visible: hasChannelOperationPermissions,
          },
          {
            id: 'share-management',
            title: t(TITLES.SHARE_SETTINGS_TITLE),
            icon: icons.managementIcon,
            path: '/admin-panel/management/share',
            visible: hasTeamsViewPermission,
          },
          {
            id: 'newsfeed-management',
            title: t(TITLES.NEWSFEED_SETTINGS_TITLE),
            icon: icons.managementIcon,
            path: '/admin-panel/management/newsfeed',
            visible: hasTeamsViewPermission,
          },
        ].filter((item) => item.visible)
      case 'mobile':
        return [
          {
            id: 'mobile-apps-settings',
            title: t(TITLES.MOBILE_APP_SETTINGS_TITLE),
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/mobile/mobile-apps',
            visible: hasSystemSettingViewPermission,
          },
          {
            id: 'in-app-browser-settings',
            title: t(FORM.IN_APP_BORWSER_SETTINGS),
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/mobile/in-app-browser',
            visible: hasSystemSettingViewPermission,
          },
        ]
/*       case 'notification':
        return [
          {
            id: 'notification-settings',
            title: 'Bildirim Ayarları(translate)',
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/notification',
            visible: hasSystemSettingViewPermission,
          },
          {
            id: 'message-notification',
            title: 'Mesaj Bildirimleri',
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/notification/message-notification',
            visible: hasSystemSettingViewPermission,
          },
          {
            id: 'newsfeed-notification',
            title: 'Haber Bildirimleri',
            icon: icons.adminSettingsIcon,
            path: '/admin-panel/notification/newsfeed-notification',
            visible: hasSystemSettingViewPermission,
          },
        ].filter((item) => item.visible) */
      default:
        return [
          {
            id: 'general-view',
            title: t(TITLES.DASHBOARD_TITLE),
            icon: icons.dashboardIcon,
            path: '/admin-panel/dashboard/general-view',
          },
        ]
    }
  }, [
    activeMenuItem, 
    hasSystemSettingViewPermission, 
    hasPasswordSettingsPermission, 
    hasEmailSettingsPermission, 
    hasLdapSettingsPermission, 
    hasRoleSettingsPermission, 
    hasUserViewPermission, 
    hasTeamsViewPermission, 
    hasChannelOperationPermissions
  ]);

  useEffect(() => {
    const path = location.pathname
    
    if (path.includes('dashboard')) {
      setActiveMenuItem('dashboard')
    } else if (path.includes('settings')) {
      setActiveMenuItem('settings')
    } else if (path.includes('management')) {
      setActiveMenuItem('management')
    } else if (path.includes('mobile')) {
      setActiveMenuItem('mobile')
    }
  }, [location.pathname])

  const subMenuItems = useMemo(() => getSubMenuItems(), [getSubMenuItems]);

  useEffect(() => {
    if (accessToken) {
      uploadApi.setHeader(accessToken)
    }
  }, [accessToken])

  return (
    <Wrapper>
      <Header/>
      <ContentLayout>
        <Sidebar 
          menuItems={menuItems as any} 
          activeMenuItem={activeMenuItem}
          setActiveMenuItem={setActiveMenuItem}
        />
        <MainContainer>
          {activeMenuItem && <Navbar subMenuItems={subMenuItems} />}
          <Content activeMenuItem={activeMenuItem} />
        </MainContainer>
      </ContentLayout>
    </Wrapper>
  )
}

export default AdminPanel

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-color: #F4F7FA;
`

const ContentLayout = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`

const MainContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 0 35px 0 35px;
` 